/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { WS_BASE_URL } from '../api/config';
import {
    attachSubAgentEvent,
    flushPendingSubAgentEvents,
    isSubAgentEvent,
    markSubAgentEventsStopped,
} from '../ai-platform/utils/subAgentEvents';

/**
 * WebSocket states
 */
const ConnectionState = {
    CONNECTING: 'CONNECTING',
    CONNECTED: 'CONNECTED',
    DISCONNECTED: 'DISCONNECTED',
    ERROR: 'ERROR',
};

const STOP_PENDING_ACTIVITY_TYPES = new Set([
    'tools_loaded',
    'iteration_started',
    'llm_thinking',
    'assistant_status',
    'assistant_chunk',
    'llm_response',
    'assistant_message',
    'request_received',
    'message_received',
    'tool_call',
    'tool_result',
    'execution_data',
]);

const DEFAULT_ACTIVITY = {
    stage: 'preparing',
    message: 'Preparing your request',
    detail: 'Setting up the assistant run.',
};

function createActivity(stage, message, detail = null) {
    return {
        stage,
        message,
        detail,
        updatedAt: new Date().toISOString(),
    };
}

function buildActivityFromStatus(data, fallback = DEFAULT_ACTIVITY) {
    return createActivity(
        data.stage || fallback.stage,
        data.message || fallback.message,
        data.detail || fallback.detail || null,
    );
}

function formatToolActivity(toolName) {
    if (!toolName) return 'Checking data';
    return toolName
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase());
}

const STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES = new Set([
    'execution_started',
    'tool_call',
    'llm_response',
]);

function shouldIgnoreDuringStop(data) {
    if (!data) return false;
    if (isSubAgentEvent(data)) {
        return STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES.has(data.type);
    }
    return STOP_PENDING_ACTIVITY_TYPES.has(data.type);
}

function createStoppedToolResult() {
    return {
        success: false,
        data: { cancelled: true },
        error: 'Execution cancelled by user.',
    };
}

function extractTraceFields(payload) {
    if (!payload) {
        return {};
    }
    const trace = payload.trace || null;
    return {
        trace,
        traceId: payload.trace_id || payload.traceId || trace?.trace_id || null,
        rootTraceId: payload.root_trace_id || payload.rootTraceId || trace?.root_trace_id || null,
        parentTraceId: payload.parent_trace_id || payload.parentTraceId || trace?.parent_trace_id || null,
    };
}

function markPendingToolCallsStopped(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return messages;

    let changed = false;
    const next = messages.map((message) => {
        let updated = message;

        if (Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0) {
            const nested = markSubAgentEventsStopped(message.subAgentEvents);
            if (nested !== message.subAgentEvents) {
                updated = { ...updated, subAgentEvents: nested };
                changed = true;
            }
        }

        if (
            (message.role === 'tool_call' || message.role === 'TOOL_CALL') &&
            (message.result === undefined || message.result === null)
        ) {
            updated = {
                ...updated,
                result: createStoppedToolResult(),
                success: false,
            };
            changed = true;
        }

        return updated;
    });

    return changed ? next : messages;
}

/**
 * Custom hook for Agent WebSocket communication.
 * 
 * Handles:
 * - WebSocket connection lifecycle
 * - Message sending/receiving
 * - Session management
 * - Automatic reconnection
 * - Streaming responses
 * 
 * @param {number|string|null} agentId - Optional agent ID. If null, connects to Main Assistant.
 * @returns {Object} Hook state and methods
 */
const useMainAssistantSocket = (agentId = null) => {
    const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
    const [agentInfo, setAgentInfo] = useState(null);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [currentPreflight, setCurrentPreflight] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isAwaitingExecutionStart, setIsAwaitingExecutionStart] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [assistantActivity, setAssistantActivity] = useState(null);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [error, setError] = useState(null);
    const [currentExecutionId, setCurrentExecutionId] = useState(null);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);

    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 5;
    const messageQueueRef = useRef([]);
    const onSessionTitleUpdateRef = useRef(null);
    const onSessionCreatedRef = useRef(null);
    const currentAgentIdRef = useRef(agentId);
    const currentSessionIdRef = useRef(null);
    const isSwitchingRef = useRef(false);
    const pendingSessionRefreshRef = useRef(false);
    const stopPendingRef = useRef(false);
    // Buffer for sub-agent events that arrive before their parent tool_call
    // has been committed to the React messages state. Keyed by
    // parent_tool_call_id. Drained by flushPendingSubAgentEvents when the
    // matching tool_call is appended.
    const pendingSubAgentEventsRef = useRef({});

    /**
     * Get WebSocket URL with auth token
     */
    const getWebSocketUrl = useCallback((targetAgentId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('[AgentSocket] No auth token found');
            return null;
        }

        const baseUrl = WS_BASE_URL || (() => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${protocol}//${window.location.host}`;
        })();

        // If agentId is provided, connect to agent-specific WebSocket
        // Otherwise, connect to Main Assistant
        if (targetAgentId) {
            return `${baseUrl}/ws/agents/${targetAgentId}/chat/?token=${encodeURIComponent(token)}`;
        }
        return `${baseUrl}/ws/main-assistant/chat/?token=${encodeURIComponent(token)}`;
    }, []);

    /**
     * Connect to WebSocket
     */
    const connect = useCallback((targetAgentId = null) => {
        // Use provided agentId or fall back to the one passed to the hook
        const effectiveAgentId = targetAgentId !== undefined ? targetAgentId : currentAgentIdRef.current;

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log('[AgentSocket] Already connected');
            return;
        }

        const url = getWebSocketUrl(effectiveAgentId);
        if (!url) {
            setError('No authentication token');
            setConnectionState(ConnectionState.ERROR);
            return;
        }

        console.log(`[AgentSocket] Connecting to ${effectiveAgentId ? `agent ${effectiveAgentId}` : 'Main Assistant'}...`);
        setConnectionState(ConnectionState.CONNECTING);
        setError(null);
        currentAgentIdRef.current = effectiveAgentId;

        try {
            socketRef.current = new WebSocket(url);

            socketRef.current.onopen = () => {
                console.log(`[AgentSocket] Connected to ${effectiveAgentId ? `agent ${effectiveAgentId}` : 'Main Assistant'}`);
                setConnectionState(ConnectionState.CONNECTED);
                reconnectAttemptsRef.current = 0;

                // Process queued messages
                while (messageQueueRef.current.length > 0) {
                    const msg = messageQueueRef.current.shift();
                    socketRef.current.send(JSON.stringify(msg));
                }
            };

            socketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleMessage(data);
                } catch (e) {
                    console.error('[AgentSocket] Failed to parse message:', e);
                }
            };

            socketRef.current.onclose = (event) => {
                console.log('[AgentSocket] Disconnected:', event.code, event.reason);
                setConnectionState(ConnectionState.DISCONNECTED);

                // Server redeploying — reconnect immediately, skip backoff
                if (event.code === 4000) {
                    console.log('[AgentSocket] Server redeploying, reconnecting immediately...');
                    reconnectAttemptsRef.current = 0;
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect(currentAgentIdRef.current);
                    }, 100);
                    return;
                }

                // Attempt reconnection if not a normal close
                if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                    console.log(`[AgentSocket] Reconnecting in ${delay}ms...`);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current++;
                        connect(currentAgentIdRef.current);
                    }, delay);
                }
            };

            socketRef.current.onerror = (event) => {
                console.error('[AgentSocket] Error:', event);
                setError('WebSocket connection error');
                setConnectionState(ConnectionState.ERROR);
            };

        } catch (e) {
            console.error('[AgentSocket] Failed to connect:', e);
            setError(e.message);
            setConnectionState(ConnectionState.ERROR);
        }
    }, [getWebSocketUrl]);

    /**
     * Disconnect from WebSocket
     */
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (socketRef.current) {
            socketRef.current.close(1000, 'User disconnected');
            socketRef.current = null;
        }

        setConnectionState(ConnectionState.DISCONNECTED);
    }, []);

    /**
     * Switch to a different agent (disconnect and reconnect)
     */
    const switchAgent = useCallback((newAgentId) => {
        console.log(`[AgentSocket] Switching to ${newAgentId ? `agent ${newAgentId}` : 'Main Assistant'}`);
        // Set flag to prevent useEffect from reconnecting during switch
        isSwitchingRef.current = true;
        // Update the agent ID ref immediately
        currentAgentIdRef.current = newAgentId;
        // Clear messages when switching agents
        setMessages([]);
        setCurrentPreflight(null);
        setCurrentSessionId(null);
        currentSessionIdRef.current = null;
        pendingSessionRefreshRef.current = false;
        stopPendingRef.current = false;
        setIsAwaitingExecutionStart(false);
        setAssistantActivity(null);
        setStreamingContent('');
        setIsTyping(false);

        // Disconnect and reconnect
        disconnect();
        setTimeout(() => {
            connect(newAgentId);
            // Clear the switching flag after connection is initiated
            setTimeout(() => {
                isSwitchingRef.current = false;
            }, 100);
        }, 100);
    }, [disconnect, connect]);

    /**
     * Handle incoming WebSocket messages
     */
    const handleMessage = useCallback((data) => {
        const { type } = data;

        if (stopPendingRef.current && shouldIgnoreDuringStop(data)) {
            console.log('[MainAssistantSocket] Ignoring late activity after stop request:', data.type);
            return;
        }

        // Route nested sub-agent events to the parent tool_call message so the
        // UI renders them inside its expandable box. If the parent tool_call
        // hasn't committed to state yet, buffer the event and flush when it
        // arrives.
        if (isSubAgentEvent(data)) {
            console.log('[MainAssistantSocket] SUB-AGENT event →', data.type,
                'parent:', data.parent_tool_call_id,
                'sub:', data.sub_agent_name);
            setMessages(prev => {
                const updated = attachSubAgentEvent(prev, data);
                if (updated === prev) {
                    const buf = pendingSubAgentEventsRef.current;
                    const key = data.parent_tool_call_id;
                    if (!buf[key]) buf[key] = [];
                    buf[key].push(data);
                    console.warn('[MainAssistantSocket] Buffered sub-agent event (parent not committed yet)',
                        { type: data.type, parent: key, bufferedCount: buf[key].length });
                }
                return updated;
            });
            return;
        }

        switch (type) {
            case 'connected':
                setAgentInfo({
                    id: data.agent_id,
                    name: data.agent_name,
                });
                setCurrentPreflight(null);
                setIsAwaitingExecutionStart(false);
                setAssistantActivity(null);
                if (data.session_id) {
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    // Automatically load session messages when connecting to an existing session
                    if (socketRef.current?.readyState === WebSocket.OPEN) {
                        console.log('[AgentSocket] Loading existing session messages:', data.session_id);
                        socketRef.current.send(JSON.stringify({
                            type: 'load_session',
                            session_id: data.session_id,
                        }));
                    }
                }
                // Check if backend reports a running execution (auto-subscribed on reconnect)
                if (data.running_execution) {
                    const re = data.running_execution;
                    console.log('[AgentSocket] Backend auto-subscribed to running execution:', re.execution_id);
                    stopPendingRef.current = false;
                    setIsAwaitingExecutionStart(false);
                    setIsTyping(true);
                    setAssistantActivity(createActivity(
                        'running',
                        'Resuming the active run',
                        'The assistant is still working on this request.',
                    ));
                    setCurrentExecutionId(re.execution_id);
                    setStreamingContent('');
                }
                break;

            case 'session_started':
                // session_started is sent on every message execution
                // Check if this is actually a NEW session (different from current OR first session for this agent)
                if (data.session_id) {
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    const wasFirstSession = currentSessionIdRef.current === null;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    // Mark that we need to refresh sessions after execution completes
                    if (isNewSession || wasFirstSession) {
                        console.log('[AgentSocket] New session detected via session_started:', data.session_id, { isNewSession, wasFirstSession });
                        pendingSessionRefreshRef.current = true;
                    }
                }
                break;

            case 'session_created':
                // session_created is only sent when a NEW session is explicitly created via "New Chat"
                console.log('[AgentSocket] session_created', data);
                if (data.session_id) {
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    setCurrentPreflight(null);
                    setIsAwaitingExecutionStart(false);
                    setAssistantActivity(null);
                    // Clear messages for new session
                    setMessages([]);
                    // Notify that a new session was created - refresh session list
                    if (onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Triggering onSessionCreated callback');
                        onSessionCreatedRef.current(data.session_id, data.title || null);
                    }
                }
                break;

            case 'execution_started':
                stopPendingRef.current = false;
                setIsAwaitingExecutionStart(false);
                setIsTyping(true);
                setAssistantActivity(buildActivityFromStatus(data, {
                    stage: 'running',
                    message: 'Starting the assistant run',
                    detail: 'The AI worker has picked up your request.',
                }));
                setStreamingContent('');
                if (data.preflight) {
                    setCurrentPreflight(data.preflight);
                }
                if (data.execution_id) {
                    setCurrentExecutionId(data.execution_id);
                }
                if (data.session_id) {
                    // Check if this is a new session using ref to avoid stale closure
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    if (isNewSession && onSessionCreatedRef.current) {
                        onSessionCreatedRef.current(data.session_id, null);
                    }
                }
                break;

            case 'message_received':
                console.log('[AgentSocket] message_received', {
                    sessionId: data.session_id,
                    currentRef: currentSessionIdRef.current,
                    isNew: data.session_id !== currentSessionIdRef.current
                });
                setIsAwaitingExecutionStart(true);
                setIsTyping(false);
                setCurrentExecutionId(null);
                setStreamingContent('');
                setAssistantActivity(buildActivityFromStatus(data, {
                    stage: 'queued',
                    message: 'Queued on AI worker',
                    detail: 'Waiting for the backend worker to start the response.',
                }));
                setCurrentPreflight(data.preflight || null);
                if (data.session_id) {
                    // Check if this is a new session using ref to avoid stale closure
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    if (isNewSession && onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Triggering onSessionCreated callback');
                        onSessionCreatedRef.current(data.session_id, data.session_title || null);
                    }
                }
                // Emit session title update event if title was set
                if (data.session_title && onSessionTitleUpdateRef.current) {
                    onSessionTitleUpdateRef.current(data.session_id, data.session_title);
                }
                break;

            case 'request_received':
            case 'assistant_status':
                setAssistantActivity(buildActivityFromStatus(data));
                if (data.preflight) {
                    setCurrentPreflight(data.preflight);
                }
                if (data.execution_id) {
                    setCurrentExecutionId(data.execution_id);
                }
                setIsAwaitingExecutionStart(data.stage !== 'running' && data.stage !== 'thinking');
                setIsTyping(data.stage === 'running' || data.stage === 'thinking');
                break;

            case 'tools_loaded':
                const toolCount = data.tool_count ?? data.count ?? 0;
                setAssistantActivity(createActivity(
                    'tools_loaded',
                    'Preparing available tools',
                    `${toolCount} tool${toolCount === 1 ? '' : 's'} ready.`,
                ));
                setIsAwaitingExecutionStart(false);
                setIsTyping(true);
                break;

            case 'iteration_started':
                setAssistantActivity(createActivity(
                    'iteration',
                    data.iteration && data.max_iterations
                        ? `Working through step ${data.iteration} of ${data.max_iterations}`
                        : 'Working through the request',
                    null,
                ));
                setIsAwaitingExecutionStart(false);
                setIsTyping(true);
                break;

            case 'llm_thinking':
                // Status updates - keep typing indicator active
                setIsAwaitingExecutionStart(false);
                setIsTyping(true);
                setAssistantActivity(createActivity(
                    'thinking',
                    'Thinking through the answer',
                    data.iteration ? `Reasoning step ${data.iteration}.` : null,
                ));
                break;

            case 'assistant_chunk':
                // Streaming text chunk
                setIsAwaitingExecutionStart(false);
                setIsTyping(true);
                setAssistantActivity(null);
                setStreamingContent(prev => prev + (data.content || ''));
                break;

            case 'llm_response':
                // Response from LLM (may be streaming chunks or final)
                if (data.is_final) {
                    stopPendingRef.current = false;
                    setIsAwaitingExecutionStart(false);
                    setIsTyping(false);
                    setAssistantActivity(null);
                    setStreamingContent('');

                    // Add assistant message to history
                    setMessages(prev => [...prev, {
                        id: data.execution_id || `assistant-${Date.now()}`,
                        role: 'assistant',
                        content: data.content,
                        timestamp: new Date().toISOString(),
                    }]);
                } else {
                    // Streaming chunk
                    setIsAwaitingExecutionStart(false);
                    setIsTyping(true);
                    setAssistantActivity(null);
                    setStreamingContent(prev => prev + (data.content || ''));
                }
                break;

            case 'assistant_message':
                // Final response (alternative format)
                stopPendingRef.current = false;
                setIsAwaitingExecutionStart(false);
                setIsTyping(false);
                setAssistantActivity(null);
                setStreamingContent('');
                setCurrentExecutionId(data.execution_id);

                // Add assistant message to history
                setMessages(prev => [...prev, {
                    id: data.execution_id || Date.now().toString(),
                    role: 'assistant',
                    content: data.content,
                    timestamp: new Date().toISOString(),
                }]);
                break;

            case 'tool_call':
                // Tool is being called
                setAssistantActivity(createActivity(
                    'tool_call',
                    formatToolActivity(data.tool_name),
                    'Checking the relevant data.',
                ));
                setMessages(prev => {
                    const next = [...prev, {
                        id: `tool-${Date.now()}`,
                        role: 'tool_call',
                        toolName: data.tool_name,
                        toolArgs: data.tool_args || data.arguments,
                        arguments: data.arguments,
                        toolCallId: data.tool_call_id, // needed to attach sub-agent events
                        subAgentEvents: [],
                        timestamp: new Date().toISOString(),
                        ...extractTraceFields(data),
                    }];
                    // Flush any nested sub-agent events that arrived before
                    // this parent tool_call committed to state.
                    return flushPendingSubAgentEvents(next, pendingSubAgentEventsRef.current);
                });
                break;

            case 'tool_result':
                // Tool execution result
                setAssistantActivity(createActivity(
                    data.result?.success === false ? 'tool_result_error' : 'tool_result',
                    data.result?.success === false ? 'Tool check needs review' : 'Data check completed',
                    'Continuing the response.',
                ));
                setMessages(prev => {
                    // Find the index of the last tool_call message
                    let lastToolCallIndex = -1;
                    for (let i = prev.length - 1; i >= 0; i--) {
                        if (prev[i].role === 'tool_call') {
                            lastToolCallIndex = i;
                            break;
                        }
                    }

                    if (lastToolCallIndex === -1) {
                        return prev;
                    }

                    // Create a new array with the updated tool call (immutable update)
                    const updated = [...prev];
                    updated[lastToolCallIndex] = {
                        ...updated[lastToolCallIndex],
                        result: data.result,
                        success: data.result?.success,
                        ...extractTraceFields(data),
                    };
                    return updated;
                });
                break;

            case 'session_loaded':
                setCurrentSessionId(data.session_id);
                setCurrentPreflight(null);
                setIsAwaitingExecutionStart(false);
                setAssistantActivity(null);
                // Convert loaded messages to our format (includes tool_call entries)
                const loadedMessages = (data.messages || []).map(msg => {
                    if (msg.role === 'tool_call') {
                        return {
                            id: msg.id,
                            role: 'tool_call',
                            toolName: msg.tool_name,
                            toolArgs: msg.tool_input,
                            result: msg.tool_output,
                            success: msg.success,
                            timestamp: msg.created_at,
                            // Reconstructed sub-agent tree (see
                            // MainAssistantChatConsumer._get_session_messages
                            // -> _build_sub_agent_events). Empty list if
                            // the tool call wasn't a delegation.
                            toolCallId: msg.tool_call_id || msg.id,
                            subAgentEvents: Array.isArray(msg.sub_agent_events)
                                ? msg.sub_agent_events
                                : [],
                            ...extractTraceFields(msg),
                        };
                    }
                    return {
                        id: msg.id,
                        role: msg.role.toLowerCase() === 'user' ? 'user' : 'assistant',
                        content: msg.content,
                        timestamp: msg.created_at,
                    };
                });
                setMessages(loadedMessages);
                setIsLoadingSession(false);
                break;

            case 'session_not_found':
                // Requested session doesn't exist anymore
                console.warn('[AgentSocket] Session not found:', data.requested_session_id);
                setIsLoadingSession(false);
                if (data.fallback_session_id) {
                    // Backend found a fallback session - use it
                    console.log('[AgentSocket] Using fallback session:', data.fallback_session_id);
                    currentSessionIdRef.current = data.fallback_session_id;
                    setCurrentSessionId(data.fallback_session_id);
                    setCurrentPreflight(null);
                    setIsAwaitingExecutionStart(false);
                    setAssistantActivity(null);
                    // Load the fallback session messages
                    const fallbackMessages = (data.messages || []).map(msg => ({
                        id: msg.id,
                        role: msg.role.toLowerCase() === 'user' ? 'user' : 'assistant',
                        content: msg.content,
                        timestamp: msg.created_at,
                    }));
                    setMessages(fallbackMessages);
                } else {
                    // No fallback session - clear everything and let user start fresh
                    console.log('[AgentSocket] No fallback session - starting fresh');
                    currentSessionIdRef.current = null;
                    setCurrentSessionId(null);
                    setCurrentPreflight(null);
                    setIsAwaitingExecutionStart(false);
                    setAssistantActivity(null);
                    setMessages([]);
                }
                // Don't refresh session list - it causes UI flashing
                break;

            case 'pending_confirmation':
                // A dangerous tool needs user confirmation
                console.log('[MainAssistantSocket] 🔔 PENDING CONFIRMATION received:', data);
                setPendingConfirmation({
                    executionId: data.execution_id,
                    toolName: data.tool_name,
                    toolArgs: data.tool_args,
                    toolCallId: data.tool_call_id,
                    dangerLevel: data.danger_level || 0,
                    message: data.message,
                });
                break;

            case 'confirmation_resolved':
                // Backend confirmed/denied the tool — clear the prompt
                setPendingConfirmation(null);
                break;

            case 'tool_confirmation_received':
                // Acknowledgement from the consumer
                break;

            case 'stop_requested':
                stopPendingRef.current = true;
                setIsAwaitingExecutionStart(false);
                setIsTyping(false);
                setAssistantActivity(null);
                setStreamingContent('');
                setPendingConfirmation(null);
                break;

            case 'execution_stopped':
                stopPendingRef.current = false;
                setIsAwaitingExecutionStart(false);
                setIsTyping(false);
                setAssistantActivity(null);
                setStreamingContent('');
                setPendingConfirmation(null);
                setCurrentExecutionId(null);
                setMessages((prev) => markPendingToolCallsStopped(prev));
                break;

            case 'execution_completed':
            case 'execution_data':
                // Execution finished - if this was a new session, NOW is the time to refresh
                // because the session is definitely saved in the database
                if (data.type === 'execution_completed') {
                    if (stopPendingRef.current && data.status !== 'CANCELLED') {
                        stopPendingRef.current = false;
                        setIsAwaitingExecutionStart(false);
                        setIsTyping(false);
                        setAssistantActivity(null);
                        setStreamingContent('');
                        setPendingConfirmation(null);
                        setCurrentExecutionId(null);
                        setMessages((prev) => markPendingToolCallsStopped(prev));
                        break;
                    }
                    stopPendingRef.current = false;
                    setIsAwaitingExecutionStart(false);
                    setIsTyping(false);
                    setAssistantActivity(null);
                    setPendingConfirmation(null);
                    setCurrentExecutionId(null);
                    // Trigger session refresh if we had a pending new session
                    if (pendingSessionRefreshRef.current && onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Execution completed, refreshing sessions for new session');
                        pendingSessionRefreshRef.current = false;
                        onSessionCreatedRef.current(currentSessionIdRef.current, null);
                    }
                }
                break;

            case 'error':
                setError(data.error);
                setIsAwaitingExecutionStart(false);
                setIsTyping(false);
                setAssistantActivity(null);
                break;

            case 'pong':
                // Heartbeat response
                break;

            default:
                console.log('[MainAssistantSocket] Unknown message type:', type, data);
        }
    }, []);

    /**
     * Send a message to the assistant
     */
    const sendMessage = useCallback((content, sessionId = null, options = {}) => {
        if (!content?.trim()) return false;

        const message = {
            type: 'message',
            content: content.trim(),
            session_id: sessionId || currentSessionId,
        };

        // Include attachment_ids if provided
        if (options.attachment_ids && options.attachment_ids.length > 0) {
            message.attachment_ids = options.attachment_ids;
        }

        if (options.route_mode) {
            message.route_mode = options.route_mode;
        }

        // Add user message to history immediately
        setCurrentPreflight(null);
        setIsAwaitingExecutionStart(true);
        setIsTyping(false);
        setAssistantActivity(createActivity(
            socketRef.current?.readyState === WebSocket.OPEN ? 'preparing' : 'connecting',
            socketRef.current?.readyState === WebSocket.OPEN
                ? 'Preparing your request'
                : 'Connecting to the assistant',
            socketRef.current?.readyState === WebSocket.OPEN
                ? 'Setting up the assistant run.'
                : 'Your message will send as soon as the chat reconnects.',
        ));
        setMessages(prev => [...prev, {
            id: `user-${Date.now()}`,
            role: 'user',
            content: content.trim(),
            timestamp: new Date().toISOString(),
        }]);

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            return true;
        } else {
            // Queue message for when connection is established
            messageQueueRef.current.push(message);
            connect();
            return false;
        }
    }, [currentSessionId, connect]);

    /**
     * Create a new chat session
     */
    const newSession = useCallback((title = '') => {
        console.log('[AgentSocket] newSession called, sending new_session message');
        const message = {
            type: 'new_session',
            title,
        };

        setCurrentPreflight(null);
        setIsAwaitingExecutionStart(false);
        setAssistantActivity(null);

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log('[AgentSocket] Sending new_session:', message);
            socketRef.current.send(JSON.stringify(message));
            // Clear messages for new session
            setMessages([]);
            return true;
        }
        console.log('[AgentSocket] newSession - WebSocket not open');
        return false;
    }, []);

    /**
     * Load an existing session
     */
    const loadSession = useCallback((sessionId) => {
        if (!sessionId) return false;

        // Set loading state
        setIsLoadingSession(true);
        setCurrentPreflight(null);
        setIsAwaitingExecutionStart(false);
        setAssistantActivity(null);
        setMessages([]); // Clear messages while loading

        const message = {
            type: 'load_session',
            session_id: sessionId,
        };

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            return true;
        }
        setIsLoadingSession(false);
        return false;
    }, []);

    /**
     * Confirm or deny a dangerous tool execution
     */
    const confirmTool = useCallback((executionId, toolName, confirmed) => {
        const message = {
            type: 'confirm_tool',
            execution_id: executionId,
            tool_name: toolName,
            confirmed,
        };

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            // Optimistically clear after denial (backend will also send confirmation_resolved)
            if (!confirmed) {
                setPendingConfirmation(null);
            }
            return true;
        }
        return false;
    }, []);

    /**
     * Stop the current execution
     */
    const stopExecution = useCallback(() => {
        const message = { type: 'stop' };

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            stopPendingRef.current = true;
            setIsTyping(false);
            setAssistantActivity(null);
            setStreamingContent('');
            setPendingConfirmation(null);
            return true;
        }
        return false;
    }, []);

    /**
     * Send a ping to keep connection alive
     */
    const ping = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    // Heartbeat to keep connection alive
    useEffect(() => {
        if (connectionState !== ConnectionState.CONNECTED) return;

        const interval = setInterval(() => {
            ping();
        }, 30000);

        return () => clearInterval(interval);
    }, [connectionState, ping]);

    // Diagnostic: expose current messages tree on window so we can inspect
    // nested tool calls from the browser console.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.__debug_messages = messages;
            window.__debug_pending_sub_agent = pendingSubAgentEventsRef.current;
        }
    }, [messages]);

    // Set callback for session title updates
    const setOnSessionTitleUpdate = useCallback((callback) => {
        onSessionTitleUpdateRef.current = callback;
    }, []);

    // Set callback for when a new session is created
    const setOnSessionCreated = useCallback((callback) => {
        onSessionCreatedRef.current = callback;
    }, []);

    return {
        // State
        connectionState,
        isConnected: connectionState === ConnectionState.CONNECTED,
        isConnecting: connectionState === ConnectionState.CONNECTING,
        isSwitching: isSwitchingRef.current,
        agentInfo,
        currentSessionId,
        currentPreflight,
        messages,
        isAwaitingExecutionStart,
        isTyping,
        assistantActivity,
        isLoadingSession,
        streamingContent,
        error,
        currentExecutionId,
        pendingConfirmation,

        // Actions
        connect,
        disconnect,
        switchAgent,
        sendMessage,
        newSession,
        loadSession,
        stopExecution,
        confirmTool,
        setOnSessionTitleUpdate,
        setOnSessionCreated,

        // Helpers
        clearMessages: () => {
            setMessages([]);
            setCurrentPreflight(null);
            setAssistantActivity(null);
        },
        clearError: () => setError(null),
        isSwitchingAgent: () => isSwitchingRef.current,
    };
};

export default useMainAssistantSocket;
export { ConnectionState };
